import type { Company, SortKey } from '@/constants/companies'

import { daysBetween, formatShortDate } from '@/lib/dates'

export type CompanyFilters = {
    sortBy: SortKey
    owner: string
    stage: string
    activityWindow: number
}

export const ALL_OWNERS = 'all'
export const ANY_STAGE = 'any'

export const DEFAULT_FILTERS: CompanyFilters = {
    sortBy: 'pipelineValue',
    owner: ALL_OWNERS,
    stage: ANY_STAGE,
    activityWindow: 90
}

export function activeFilterCount({ owner, stage, activityWindow }: CompanyFilters): number {
    return [
        owner !== DEFAULT_FILTERS.owner,
        stage !== DEFAULT_FILTERS.stage,
        activityWindow !== DEFAULT_FILTERS.activityWindow
    ].filter(Boolean).length
}

const TAG_CHAR_BUDGET = 20

export function filterCompanies(
    companies: Company[],
    { sortBy, owner, stage, activityWindow }: CompanyFilters
): Company[] {
    const filtered = companies.filter(company => {
        if (owner !== ALL_OWNERS && company.owner !== owner) return false
        if (stage !== ANY_STAGE && !company.tags.some(tag => tag === stage)) return false

        return company.activityDays <= activityWindow
    })

    return filtered.sort((a, b) => {
        switch (sortBy) {
            case 'name':
                return a.name.localeCompare(b.name)
            case 'lastInteraction':
                return b.lastInteraction.date.localeCompare(a.lastInteraction.date)
            case 'openDeals':
                return b.openDeals - a.openDeals
            case 'winProbability':
                return b.winProbability - a.winProbability
            default:
                return b.pipelineValue - a.pipelineValue
        }
    })
}

export function companiesCsvRows(companies: Company[]): Array<Array<string | number>> {
    return [
        [
            "Company",
            "Segment & Stage",
            "Account Owner",
            "Open Deals",
            "Pipeline Value",
            "Win Probability (%)",
            "Last Interaction Date",
            "Last Interaction"
        ],
        ...companies.map(company => [
            company.name,
            company.tags.join('; '),
            company.owner,
            company.openDeals,
            company.pipelineValue,
            company.winProbability,
            company.lastInteraction.date,
            company.lastInteraction.label
        ])
    ]
}

export function splitTags(tags: string[]): { visible: string[], hidden: number } {
    let used = 0
    const visible: string[] = []

    for (const tag of tags) {
        if (visible.length === 2 || used + tag.length > TAG_CHAR_BUDGET) break
        visible.push(tag)
        used += tag.length
    }

    if (visible.length === 0 && tags.length > 0) visible.push(tags[0])

    return { visible, hidden: tags.length - visible.length }
}

export function companyHealth(company: Company): { discovery: number, evaluation: number, procurement: number } {
    return {
        discovery: Math.round(company.winProbability * 0.372),
        evaluation: Math.round(company.winProbability * 0.651),
        procurement: Math.round(company.winProbability * 0.372)
    }
}

export function companyActivity(company: Company): { total: number, touches: number, emails: number, meetings: number, calls: number } {
    const deals = company.openDeals

    return {
        total: deals * 15,
        touches: deals * 4,
        emails: deals + 4,
        meetings: Math.ceil(deals / 2),
        calls: deals + 1
    }
}

export function formatDate(iso: string): string {
    return formatShortDate(iso)
}

export function formatMoney(value: number): string {
    return value.toLocaleString('en-US')
}

export function formatCompactMoney(value: number): string {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(value >= 10000000 ? 0 : 1).replace(/\.0$/, '')}M`
    if (value >= 1000) return `$${Math.round(value / 1000)}K`

    return `$${value}`
}

export function daysSince(iso: string, today: string): number {
    return Math.max(0, daysBetween(iso, today))
}
